<template>
  <div class="pricing">
    <b-card no-body>
      <div class="own-header">
        <i class="fa fa-gbp"></i>
        <strong class="header-spacing"> Prices</strong>
      </div>

      <b-tabs v-model="mainTab" card class="pricing__main-tabs">
        <!-- ===================== PRICES ===================== -->
        <b-tab title="Prices">
          <div v-if="loading" class="pricing__muted"><b-spinner small /> Loading prices…</div>

          <b-alert v-else-if="loadError" show variant="danger">
            {{ loadError }}
            <b-button size="sm" variant="outline-danger" class="ml-2" @click="load">Try again</b-button>
          </b-alert>

          <div v-else-if="!platforms.length" class="pricing__muted">No prices found.</div>

          <template v-else>
            <div class="pricing__picker">
              <label for="pricing-website" class="pricing__picker-label">Which website's prices?</label>
              <b-form-select id="pricing-website" v-model="activeId" :options="websiteOptions" class="pricing__picker-select" />
            </div>

            <p class="pricing__muted mb-3">
              Type a new price in any box. Nothing changes for patients until you press <strong>Save changes</strong>.
            </p>

            <div v-if="current">
              <!-- Main prices -->
              <div class="pricing__list">
                <price-row v-for="field in mainFields" :key="field.key" v-bind="rowProps(field)" large
                           @input="setDraft(field.key, $event)" />
              </div>

              <!-- Collapsible sections -->
              <div v-for="section in extraSections" :key="section.id" class="pricing__section">
                <button type="button" class="pricing__section-toggle" :aria-expanded="open[section.id] ? 'true' : 'false'"
                        @click="toggle(section.id)">
                  <i :class="open[section.id] ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
                  <span class="pricing__section-title">{{ section.title }}</span>
                  <span v-if="sectionChanges(section)" class="pricing__badge">{{ sectionChanges(section) }} changed</span>
                  <span v-if="sectionErrors(section)" class="pricing__badge pricing__badge--error">please check</span>
                  <span class="pricing__section-show">{{ open[section.id] ? 'Hide' : 'Show' }}</span>
                </button>
                <b-collapse :id="'pricing-section-' + section.id" :visible="!!open[section.id]">
                  <div class="pricing__list pricing__list--compact">
                    <price-row v-for="field in section.fields" :key="field.key" v-bind="rowProps(field)"
                               @input="setDraft(field.key, $event)" />
                  </div>
                </b-collapse>
              </div>
            </div>
          </template>
        </b-tab>

        <!-- ===================== CHANGE HISTORY ===================== -->
        <b-tab title="Change history">
          <div class="pricing__history-bar">
            <b-form-select v-model="historyWebsite" :options="historyWebsiteOptions" size="sm" class="pricing__history-filter"
                           aria-label="Website" @change="loadHistory(0)" />
            <b-button size="sm" variant="outline-secondary" :disabled="historyLoading" @click="loadHistory(historyPage)">
              <i class="fa fa-refresh"></i> Refresh
            </b-button>
          </div>

          <b-alert v-if="historyError" show variant="danger">
            {{ historyError }}
            <b-button size="sm" variant="outline-danger" class="ml-2" @click="loadHistory(historyPage)">Try again</b-button>
          </b-alert>

          <div v-else-if="historyLoading && !historyItems.length" class="pricing__muted"><b-spinner small /> Loading…</div>

          <div v-else-if="!historyItems.length" class="pricing__empty">
            No price changes yet. Every change made on the Prices tab will be listed here with who made it and when.
          </div>

          <template v-else>
            <b-table :items="historyRows" :fields="historyFields" :tbody-tr-class="historyRowClass"
                     small responsive hover class="pricing__history">
              <template #cell(when)="row">
                <div>{{ row.item.whenText }}</div>
                <small class="pricing__muted">{{ row.item.whenAgo }}</small>
              </template>
              <template #cell(before)="row"><span class="pricing__muted">{{ row.item.before }}</span></template>
              <template #cell(after)="row"><strong>{{ row.item.after }}</strong></template>
            </b-table>

            <div class="pricing__pager">
              <span class="pricing__muted">Showing {{ historyFrom }}–{{ historyTo }} of {{ historyTotal }}</span>
              <div>
                <b-button size="sm" variant="outline-secondary" :disabled="historyLoading || historyPage === 0"
                          @click="loadHistory(historyPage - 1)">Previous</b-button>
                <b-button size="sm" variant="outline-secondary" class="ml-1" :disabled="historyLoading || historyTo >= historyTotal"
                          @click="loadHistory(historyPage + 1)">Next</b-button>
              </div>
            </div>
          </template>
        </b-tab>
      </b-tabs>
    </b-card>

    <!-- Sticky save bar -->
    <div v-if="changeCount" class="pricing__savebar">
      <span class="pricing__savebar-text">
        You have changed {{ changeCount }} {{ changeCount === 1 ? 'price' : 'prices' }}
        <span v-if="dirtyPlatformIds.length > 1">on {{ dirtyPlatformIds.length }} websites</span>
      </span>
      <div>
        <b-button variant="outline-secondary" :disabled="saving" @click="discard">Undo</b-button>
        <b-button variant="success" size="lg" class="ml-2" :disabled="saving" @click="confirmSave">
          <b-spinner v-if="saving" small /> Save changes
        </b-button>
      </div>
    </div>

    <b-modal ref="confirm" title="Save these new prices?" size="lg" centered
             ok-title="Yes, save prices" ok-variant="success" cancel-title="Go back"
             :ok-disabled="saving" @ok="save">
      <p>New prices apply straight away to new bookings across the website, the doctor app and emails.</p>
      <div v-for="group in pendingChanges" :key="group.platformId" class="mb-3">
        <h6 class="mb-1">{{ group.name }}</h6>
        <table class="table table-sm mb-0">
          <thead>
            <tr><th>Price</th><th>Before</th><th>After</th></tr>
          </thead>
          <tbody>
            <tr v-for="change in group.changes" :key="change.key">
              <td>{{ change.label }}</td>
              <td class="text-muted">{{ change.from }}</td>
              <td><strong>{{ change.to }}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';

/** One price: label, plain hint, input with £ (or %), "was £X" when changed, and a plain error. */
const PriceRow = {
  name: 'PriceRow',
  props: {
    id: String,
    label: String,
    hint: String,
    value: String,
    percent: Boolean,
    changed: Boolean,
    was: String,
    error: String,
    disabled: Boolean,
    large: Boolean
  },
  render (h) {
    const input = h('b-form-input', {
      props: { id: this.id, value: this.value, type: 'number', step: this.percent ? '1' : '0.01', min: '0', disabled: this.disabled, state: this.error ? false : null, placeholder: 'Not set' },
      class: { 'pricing__input--changed': this.changed },
      on: { input: v => this.$emit('input', v) }
    });
    return h('div', { class: ['pricing__row', { 'pricing__row--large': this.large, 'pricing__row--changed': this.changed }] }, [
      h('div', { class: 'pricing__row-text' }, [
        h('label', { attrs: { for: this.id }, class: 'pricing__row-label' }, this.label),
        this.hint ? h('div', { class: 'pricing__muted' }, this.hint) : null
      ]),
      h('div', { class: 'pricing__row-input' }, [
        h('b-input-group', { props: { prepend: this.percent ? null : '£', append: this.percent ? '%' : null, size: this.large ? 'lg' : null } }, [input]),
        this.changed ? h('div', { class: 'pricing__was' }, 'was ' + this.was) : null,
        this.error ? h('div', { class: 'text-danger pricing__error' }, this.error) : null
      ])
    ]);
  }
};

const MAIN_FIELDS = [
  { key: 'appointmentPrice', label: 'GP consultation', hint: 'What the patient pays for a standard video or phone consultation.' },
  { key: 'documentFee', label: 'Prescription, fit note or letter', hint: 'Added only if the doctor issues one of these.' },
  { key: 'appointmentPreAuthPrice', label: 'Amount held on the card when booking', hint: 'Held (not taken) on the patient\'s card when they book. Must be at least the consultation price.' },
  { key: 'repeatPrescriptionFee', label: 'Repeat prescription', hint: 'Charged each time a repeat prescription is issued.' },
  { key: 'medicationDeliveryPrice', label: 'Medicine home delivery', hint: 'Delivery charge when medicine is posted to the patient.' },
  { key: 'coldChainFee', label: 'Fridge medicine delivery (extra)', hint: 'Added when the medicine must be kept cold.' },
  { key: 'lateCancellationFee', label: 'Late cancellation fee', hint: 'Charged if a patient cancels between 24 and 3 hours before the appointment. Enter 0 for no charge.' },
  { key: 'chatAssessmentPrice', label: 'Online chat assessment', hint: '' },
  { key: 'appointmentExtensionPrice', label: 'Extending a consultation', hint: 'If the consultation runs over and is extended.' }
];

const EXTRA_SECTIONS = [
  {
    id: 'holiday',
    title: 'Bank holiday prices',
    fields: [
      { key: 'holidayAppointmentPrice', label: 'Bank holiday consultation', hint: '' },
      { key: 'holidayAppointmentPreAuthPrice', label: 'Bank holiday – amount held on card', hint: 'Must be at least the bank holiday consultation price.' },
      { key: 'holidayAppointmentExtensionPrice', label: 'Bank holiday – extending a consultation', hint: '' }
    ]
  },
  {
    id: 'documents',
    title: 'Documents issued during a consultation',
    fields: [
      { key: 'prescriptionPrice', label: 'Prescription', hint: '' },
      { key: 'sickNotePrice', label: 'Fit (sick) note', hint: '' },
      { key: 'referralLetterPrice', label: 'Referral letter', hint: '' }
    ]
  },
  {
    id: 'advanced',
    title: 'Advanced – medicine pricing (ask the tech team before changing)',
    fields: [
      { key: 'formPrice', label: 'Online questionnaire', hint: '' },
      { key: 'drugMinimumCharge', label: 'Minimum medicine charge', hint: '' },
      { key: 'drugProfessionalFee', label: 'Pharmacy professional fee', hint: '' },
      { key: 'drugMarkupFactor', label: 'Medicine markup %', hint: 'Added on top of what the medicine costs us, e.g. 20 means 20% extra.', percent: true }
    ]
  }
];

const FIELDS = MAIN_FIELDS.concat(...EXTRA_SECTIONS.map(s => s.fields));
const FIELD_BY_KEY = FIELDS.reduce((map, f) => { map[f.key] = f; return map; }, {});

/** Card hold must be at least the matching consultation price. */
const HOLD_RULES = [
  { hold: 'appointmentPreAuthPrice', price: 'appointmentPrice', message: 'The card hold must be at least the consultation price' },
  { hold: 'holidayAppointmentPreAuthPrice', price: 'holidayAppointmentPrice', message: 'The bank holiday card hold must be at least the bank holiday consultation price' }
];

const FRIENDLY_NAMES = { GPS: 'The GP Service (main website)', COOP: 'Co-op', SUPERDRUG: 'Superdrug' };

const PAGE_SIZE = 50;

const isBlank = v => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
const round = (n, dp) => { const f = Math.pow(10, dp); return Math.round(n * f) / f; };

/** Value as typed (string) → value the server stores (number or null; NaN if not a number). Markup: 20 → 0.2. */
function toServer (field, typed) {
  if (isBlank(typed)) {
    return null;
  }
  const n = Number(typed);
  if (!Number.isFinite(n)) {
    return NaN;
  }
  return field.percent ? round(n / 100, 6) : n;
}

/** Server value → text for the input. Markup: 0.2 → "20". */
function toTyped (field, value) {
  if (isBlank(value) || !Number.isFinite(Number(value))) {
    return '';
  }
  return field.percent ? String(round(Number(value) * 100, 4)) : String(value);
}

function same (a, b) {
  const x = isBlank(a) ? null : Number(a);
  const y = isBlank(b) ? null : Number(b);
  if (x === null || y === null) {
    return x === y;
  }
  return Math.abs(x - y) < 1e-9;
}

function display (field, value) {
  if (isBlank(value)) {
    return 'not set';
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return String(value);
  }
  return field && field.percent ? round(n * 100, 2) + '%' : '£' + n.toFixed(2);
}

export default {
  name: 'Pricing',
  components: { PriceRow },
  data () {
    return {
      mainTab: 0,
      loading: true,
      loadError: '',
      saving: false,
      platforms: [],
      drafts: {},
      activeId: 'GPS',
      serverErrors: {},
      open: { holiday: false, documents: false, advanced: false },
      mainFields: MAIN_FIELDS,
      extraSections: EXTRA_SECTIONS,
      leavingAnyway: false,

      historyLoaded: false,
      historyLoading: false,
      historyError: '',
      historyItems: [],
      historyTotal: 0,
      historyPage: 0,
      historyWebsite: null,
      historyFields: [
        { key: 'when', label: 'When' },
        { key: 'who', label: 'Who' },
        { key: 'website', label: 'Website' },
        { key: 'price', label: 'Price' },
        { key: 'before', label: 'Before' },
        { key: 'after', label: 'After' }
      ]
    };
  },
  computed: {
    current () {
      return this.platforms.find(p => p.platformId === this.activeId) || null;
    },
    websiteOptions () {
      return this.platforms.map(p => ({
        value: p.platformId,
        text: this.websiteName(p) + (this.changedFields(p.platformId).length ? ' – unsaved changes' : '')
      }));
    },
    historyWebsiteOptions () {
      return [{ value: null, text: 'All websites' }].concat(this.platforms.map(p => ({ value: p.platformId, text: this.websiteName(p) })));
    },
    dirtyPlatformIds () {
      return this.platforms.filter(p => this.changedFields(p.platformId).length).map(p => p.platformId);
    },
    changeCount () {
      return this.platforms.reduce((count, p) => count + this.changedFields(p.platformId).length, 0);
    },
    /** { platformId: { fieldKey: message } } — plain-word problems, client checks plus the server's 400 fields. */
    // Not called `errors`: vee-validate adds its own `errors` to every component, which would hide this one.
    priceErrors () {
      const all = {};
      this.platforms.forEach(p => {
        const id = p.platformId;
        const draft = this.drafts[id] || {};
        const errs = {};
        FIELDS.forEach(field => {
          const typed = draft[field.key];
          const value = toServer(field, typed);
          const example = field.percent ? 'Please enter a percentage, e.g. 20' : 'Please enter a price, e.g. 39.00';
          if (value === null) {
            if (!isBlank(p[field.key])) {
              errs[field.key] = example;
            }
          } else if (!Number.isFinite(value) || value < 0) {
            errs[field.key] = example;
          }
        });
        HOLD_RULES.forEach(rule => {
          const hold = toServer(FIELD_BY_KEY[rule.hold], draft[rule.hold]);
          const price = toServer(FIELD_BY_KEY[rule.price], draft[rule.price]);
          if (!errs[rule.hold] && Number.isFinite(hold) && Number.isFinite(price) && hold !== null && price !== null && hold < price) {
            errs[rule.hold] = rule.message + ' (£' + price.toFixed(2) + ').';
          }
        });
        FIELDS.forEach(field => {
          const server = this.serverErrors[id + '.' + field.key];
          if (server && !errs[field.key]) {
            errs[field.key] = server;
          }
        });
        all[id] = errs;
      });
      return all;
    },
    pendingChanges () {
      return this.dirtyPlatformIds.map(id => {
        const p = this.platforms.find(x => x.platformId === id);
        return {
          platformId: id,
          name: this.websiteName(p),
          changes: this.changedFields(id).map(field => ({
            key: field.key,
            label: field.label,
            from: display(field, p[field.key]),
            to: display(field, toServer(field, this.drafts[id][field.key]))
          }))
        };
      });
    },
    historyRows () {
      let group = -1;
      let last;
      return this.historyItems.map((item, idx) => {
        const key = item.changeGroup || ('single-' + (item.id || idx));
        const first = key !== last;
        if (first) {
          group += 1;
          last = key;
        }
        const field = FIELD_BY_KEY[item.field];
        const when = item.changedAt ? moment(item.changedAt) : null;
        return {
          id: item.id || idx,
          whenText: when ? when.local().format('D MMM YYYY, HH:mm') : '',
          whenAgo: when ? when.fromNow() : '',
          who: item.adminName || item.adminEmail || (item.adminId ? 'Admin #' + item.adminId : 'Admin'),
          website: this.websiteName({ platformId: item.platformId, platformName: item.platformName }),
          price: (field && field.label) || item.fieldLabel || item.field,
          before: display(field, item.oldValue),
          after: display(field, item.newValue),
          groupOdd: group % 2 === 1,
          groupStart: first && idx > 0
        };
      });
    },
    historyFrom () {
      return this.historyTotal ? this.historyPage * PAGE_SIZE + 1 : 0;
    },
    historyTo () {
      return Math.min(this.historyTotal, this.historyPage * PAGE_SIZE + this.historyItems.length);
    }
  },
  watch: {
    mainTab (tab) {
      if (tab === 1 && !this.historyLoaded) {
        this.loadHistory(0);
      }
    }
  },
  created () {
    this.load();
    window.addEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeRouteLeave (to, from, next) {
    if (this.leavingAnyway || !this.changeCount) {
      next();
      return;
    }
    this.$bvModal.msgBoxConfirm('You have changed some prices but not saved them. Leave this page and lose the changes?', {
      title: 'Prices not saved', okTitle: 'Leave without saving', okVariant: 'danger', cancelTitle: 'Stay on this page', centered: true
    }).then(ok => next(!!ok)).catch(() => next(false));
  },
  methods: {
    websiteName (p) {
      return (p && FRIENDLY_NAMES[p.platformId]) || (p && (p.platformName || p.platformId)) || '';
    },
    load () {
      this.loading = true;
      this.loadError = '';
      this.$pricingService.list().then(list => {
        this.apply(list || []);
        if (!this.current && this.platforms.length) {
          this.activeId = this.platforms[0].platformId;
        }
      }).catch(error => {
        if (this.signedOut(error)) {
          return;
        }
        this.loadError = this.$pricingService.errorMessage(error, 'Could not load the prices. Please try again.');
      }).finally(() => {
        this.loading = false;
      });
    },
    /** Take the server's values for every platform in `list` (others kept as they are). */
    apply (list) {
      const byId = {};
      this.platforms.forEach(p => { byId[p.platformId] = p; });
      const drafts = Object.assign({}, this.drafts);
      list.forEach(p => {
        if (p && p.platformId) {
          byId[p.platformId] = p;
          drafts[p.platformId] = this.draftFrom(p);
        }
      });
      const platforms = Object.keys(byId).map(id => byId[id]);
      platforms.forEach(p => { if (!drafts[p.platformId]) { drafts[p.platformId] = this.draftFrom(p); } });
      platforms.sort((a, b) => {
        if (a.platformId === 'GPS') { return -1; }
        if (b.platformId === 'GPS') { return 1; }
        return this.websiteName(a).localeCompare(this.websiteName(b));
      });
      this.platforms = platforms;
      this.drafts = drafts;
    },
    draftFrom (platform) {
      const draft = {};
      FIELDS.forEach(field => { draft[field.key] = toTyped(field, platform[field.key]); });
      return draft;
    },
    setDraft (key, value) {
      this.$set(this.drafts[this.activeId], key, value);
      const serverKey = this.activeId + '.' + key;
      if (this.serverErrors[serverKey]) {
        this.$delete(this.serverErrors, serverKey);
      }
    },
    changedFields (platformId) {
      const platform = this.platforms.find(p => p.platformId === platformId);
      const draft = this.drafts[platformId];
      if (!platform || !draft) {
        return [];
      }
      return FIELDS.filter(field => !same(platform[field.key], toServer(field, draft[field.key])));
    },
    rowProps (field) {
      const p = this.current;
      const id = p.platformId;
      const changed = !same(p[field.key], toServer(field, this.drafts[id][field.key]));
      return {
        id: 'price-' + id + '-' + field.key,
        label: field.label,
        hint: field.hint,
        value: this.drafts[id][field.key],
        percent: !!field.percent,
        changed,
        was: display(field, p[field.key]),
        error: (this.priceErrors[id] || {})[field.key] || '',
        disabled: this.saving
      };
    },
    toggle (id) {
      this.$set(this.open, id, !this.open[id]);
    },
    sectionChanges (section) {
      if (!this.current) { return 0; }
      const changed = this.changedFields(this.current.platformId).map(f => f.key);
      return section.fields.filter(f => changed.includes(f.key)).length;
    },
    sectionErrors (section) {
      if (!this.current) { return 0; }
      const errs = this.priceErrors[this.current.platformId] || {};
      return section.fields.filter(f => errs[f.key]).length;
    },
    /** Show the first website with a problem and open the sections it is in. */
    showProblems (platformIds) {
      const id = platformIds.find(pid => Object.keys(this.priceErrors[pid] || {}).length);
      if (!id) {
        return false;
      }
      this.activeId = id;
      this.mainTab = 0;
      const errs = this.priceErrors[id];
      EXTRA_SECTIONS.forEach(section => {
        if (section.fields.some(f => errs[f.key])) {
          this.$set(this.open, section.id, true);
        }
      });
      return true;
    },
    confirmSave () {
      if (!this.changeCount) {
        return;
      }
      if (this.showProblems(this.dirtyPlatformIds)) {
        this.$awn.warning('Some prices need checking – they are marked in red.');
        return;
      }
      this.$refs.confirm.show();
    },
    save () {
      const payload = this.dirtyPlatformIds.map(id => {
        const platform = this.platforms.find(p => p.platformId === id);
        const row = { platformId: id, platformName: platform.platformName === undefined ? null : platform.platformName };
        FIELDS.forEach(field => { row[field.key] = toServer(field, this.drafts[id][field.key]); });
        return row;
      });
      if (!payload.length) {
        return;
      }
      this.saving = true;
      this.serverErrors = {};
      this.$pricingService.save(payload).then(list => {
        if (Array.isArray(list) && list.length) {
          this.apply(list);
        } else {
          this.load();
        }
        this.$awn.success('Prices saved');
        this.historyLoaded = false;
        this.loadHistory(0);
      }).catch(error => {
        if (this.signedOut(error)) {
          return;
        }
        const fields = this.$pricingService.fieldErrors(error);
        if (Object.keys(fields).length) {
          this.serverErrors = Object.assign({}, fields);
          this.$nextTick(() => this.showProblems(this.dirtyPlatformIds));
        }
        this.$awn.alert(this.$pricingService.errorMessage(error, 'The prices could not be saved. Please try again.'));
      }).finally(() => {
        this.saving = false;
      });
    },
    discard () {
      const drafts = {};
      this.platforms.forEach(p => { drafts[p.platformId] = this.draftFrom(p); });
      this.drafts = drafts;
      this.serverErrors = {};
    },

    loadHistory (page) {
      this.historyLoading = true;
      this.historyError = '';
      const params = { page: page || 0, size: PAGE_SIZE };
      if (this.historyWebsite) {
        params.platformId = this.historyWebsite;
      }
      this.$pricingService.activity(params).then(result => {
        const body = result || {};
        this.historyItems = body.items || [];
        this.historyTotal = Number(body.total) || 0;
        this.historyPage = Number(body.page) || params.page;
        this.historyLoaded = true;
      }).catch(error => {
        if (this.signedOut(error)) {
          return;
        }
        this.historyError = this.$pricingService.errorMessage(error, 'Could not load the change history. Please try again.');
      }).finally(() => {
        this.historyLoading = false;
      });
    },
    historyRowClass (item, type) {
      if (!item || type !== 'row') {
        return '';
      }
      return [item.groupOdd ? 'pricing__group--odd' : '', item.groupStart ? 'pricing__group--start' : ''];
    },

    signedOut (error) {
      if (!this.$pricingService.isSignedOut(error)) {
        return false;
      }
      this.leavingAnyway = true;
      this.$root.logout();
      return true;
    },
    onBeforeUnload (event) {
      if (this.changeCount) {
        event.preventDefault();
        event.returnValue = '';
      }
    }
  }
};
</script>

<style scoped>
/* "Prices" / "Change history" tabs: the admin theme turns the tab dark slate on hover while the text stays
   dark, so the label disappears. Keep a light background and dark text on hover; the open tab stays bold. */
.pricing__main-tabs >>> .nav-tabs .nav-link {
  color: #3e4b52;
  font-weight: 500;
}
.pricing__main-tabs >>> .nav-tabs .nav-link:hover,
.pricing__main-tabs >>> .nav-tabs .nav-link:focus {
  color: #151b1e;
  /* !important: _custom.scss sets every .nav-link:hover to #465a63 !important (meant for the dark sidebar) */
  background-color: #e8f3ee !important;
  border-color: #c2cfd6 #c2cfd6 transparent;
}
.pricing__main-tabs >>> .nav-tabs .nav-link.active,
.pricing__main-tabs >>> .nav-tabs .nav-link.active:hover {
  color: #151b1e;
  background-color: #fff !important;
  font-weight: 600;
}

.pricing {
  padding-bottom: 5rem;
}
.pricing__muted {
  color: #64748b;
  font-size: 0.9rem;
}
.pricing__picker {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}
.pricing__picker-label {
  font-weight: 600;
  font-size: 1.05rem;
  margin: 0;
}
.pricing__picker-select {
  max-width: 360px;
}
.pricing__list {
  border: 1px solid #e5ebe8;
  border-radius: 6px;
}
.pricing__list--compact {
  margin-top: 0.5rem;
}
.pricing__section {
  margin-top: 1rem;
}
.pricing__section-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  text-align: left;
  border: 1px solid #e5ebe8;
  border-radius: 6px;
  background: #f8fafc;
  padding: 0.75rem 1rem;
  cursor: pointer;
}
.pricing__section-title {
  font-weight: 600;
}
.pricing__section-show {
  margin-left: auto;
  color: #0f6a7c;
  font-size: 0.9rem;
}
.pricing__badge {
  background: #fff1e0;
  color: #b45309;
  border-radius: 999px;
  padding: 0.05rem 0.55rem;
  font-size: 0.8rem;
}
.pricing__badge--error {
  background: #fde8e8;
  color: #c0392b;
}
.pricing__savebar {
  position: sticky;
  bottom: 0;
  z-index: 20;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  background: #fff;
  border: 1px solid #f3c58b;
  border-radius: 6px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.08);
  padding: 0.75rem 1rem;
  margin-top: 1rem;
}
.pricing__savebar-text {
  font-weight: 600;
  font-size: 1.05rem;
}
.pricing__history-bar {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;
}
.pricing__history-filter {
  max-width: 300px;
}
.pricing__empty {
  color: #64748b;
  padding: 2rem 1rem;
  text-align: center;
  border: 1px dashed #d5dde0;
  border-radius: 6px;
}
.pricing__pager {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.pricing__history >>> .pricing__group--odd {
  background: #f6f9fb;
}
.pricing__history >>> .pricing__group--start td {
  border-top: 2px solid #d5dde0;
}
.pricing >>> .pricing__row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5ebe8;
}
.pricing >>> .pricing__row:last-child {
  border-bottom: 0;
}
.pricing >>> .pricing__row--large {
  padding: 1rem;
}
.pricing >>> .pricing__row--changed {
  background: #fff8f0;
}
.pricing >>> .pricing__row-text {
  flex: 1 1 320px;
}
.pricing >>> .pricing__row-label {
  font-weight: 600;
  margin: 0;
}
.pricing >>> .pricing__row--large .pricing__row-label {
  font-size: 1.1rem;
}
.pricing >>> .pricing__row-input {
  flex: 0 0 220px;
  max-width: 100%;
}
.pricing >>> .pricing__input--changed {
  border-color: #e67e22;
}
.pricing >>> .pricing__was {
  color: #b45309;
  font-size: 0.85rem;
  margin-top: 0.2rem;
}
.pricing >>> .pricing__error {
  font-size: 0.85rem;
  margin-top: 0.2rem;
}
</style>
